import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, Zap, X } from "lucide-react";
import AppShell from "../components/AppShell";
import LegalFooter from "../components/LegalFooter";
import { api } from "../lib/api";
import { useEntitlements, invalidateEntitlements } from "../lib/entitlements";

const PLANS = [
  {
    id: "independiente",
    name: "Independiente",
    price: 299,
    tagline: "Perfecto para freelancers y creadores.",
    features: [
      "Finanzas · Ingresos · Egresos · Caja",
      "Facturación CFDI 4.0",
      "Hasta 50 facturas por ciclo de facturación",
      "Calendario y Declaraciones",
      "Proyectos y Reportes",
      "Academia contable",
      "Mi Contador IA (chat)",
    ],
    excluded: ["Escáner OCR", "Clientes / Proveedores", "Nómina", "Bóveda", "Portafolio"],
  },
  {
    id: "empresa",
    name: "Empresa",
    price: 699,
    tagline: "Para negocios y equipos que crecen.",
    highlight: true,
    features: [
      "Todo lo de Independiente, más:",
      "Hasta 100 facturas por ciclo de facturación",
      "Escáner de tickets (OCR con IA)",
      "Clientes y Proveedores",
      "Nómina completa",
      "Mi Bóveda · Notas · Inventario · Productos",
      "Facturas Enviadas y Recibidas",
    ],
    excluded: ["Portafolio", "Multi-Empresa"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 1499,
    tagline: "Sin límites. Múltiples empresas + Contador Real",
    features: [
      "Todo lo de Empresa, más:",
      "Hasta 250 facturas por ciclo de facturación",
      "Portafolio · Patrimonio · Activos",
      "Múltiples empresas en una cuenta",
      "Dashboards independientes por empresa",
      "Cambio instantáneo entre empresas",
      "Soporte prioritario",
      "Asesoría contable personalizada: 2 sesiones de 1 hora al mes",
      "Prueba gratuita no incluye Contador Real.",
    ],
  },
  {
    id: "privado",
    name: "Mi Contador Privado",
    price: 3500,
    tagline: "Software + IA + Contador Real",
    subLabel: "(Persona Física)",
    moralNote: "(Persona Moral agenda una cita para evaluar tu situación)",
    intro: "Todo Mi Contador. Más un equipo real que te ayuda a manejar la parte financiera y fiscal de tu negocio.",
    features: [
      "Todas las herramientas de Mi Contador",
      "Contador personal asignado",
      "Asesoría contable: 1 hora semanal",
      "Gestión y supervisión de tesorería",
      "Conciliación bancaria",
      "Organización financiera",
      "Creación de facturas",
      "Preparación de declaraciones",
      "Seguimiento fiscal",
      "Soporte prioritario",
      "Mi Contador IA + soporte humano",
      "Prueba gratuita no incluye Contador Real.",
    ],
    footerNote: "Tú manejas tu negocio. Nosotros te ayudamos con los números.",
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { status, refresh } = useEntitlements();
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [interval, setInterval] = useState("monthly");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  // Privado onboarding modal — Persona Moral flow.
  const [showPrivadoModal, setShowPrivadoModal] = useState(false);

  useEffect(() => { if (search.get("trial") === "1") setNotice("¿Quieres probar todo antes? Empieza tu prueba de 7 días abajo."); }, [search]);

  const startTrial = async () => {
    setBusy("trial"); setError(""); setNotice("");
    try {
      await api.post("/subscription/start-trial", {}, { withCredentials: true });
      invalidateEntitlements();
      await refresh();
      setNotice("¡Prueba activada! Tienes 7 días para explorar todo sin límites.");
      setTimeout(() => navigate("/"), 1200);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos activar tu prueba.");
    } finally { setBusy(""); }
  };

  const selectPlan = async (planId, paymentType = "monthly_recurring") => {
    // Route through the native Mi Contador checkout page — it reviews the
    // plan, shows Mercado Pago's supported methods, and redirects to MP.
    navigate(`/checkout?plan=${planId}&type=${paymentType}`);
  };

  const redeem = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true); setError(""); setNotice("");
    try {
      const r = await api.post("/subscription/redeem-promo", { code: promoCode.trim() }, { withCredentials: true });
      invalidateEntitlements();
      await refresh();
      setNotice(`Código aplicado: ${r.data.promo_applied || "descuento activo"}. 🎉`);
      setPromoCode("");
    } catch (e) {
      setError(e.response?.data?.detail || "El código no es válido.");
    } finally { setPromoBusy(false); }
  };

  const trialUsed = status?.trial_used;
  const currentPlan = status?.plan;
  const discount = status?.promo_discount || 0;

  return (
    <AppShell>
      <div className="pricing-page" data-testid="pricing-page">
        <section className="pricing-hero">
          <small className="eyebrow">PLANES</small>
          <h1>Elige el plan que <em>crece contigo.</em></h1>
          <p>Todo se cancela cuando quieras. Tus datos siempre están seguros.</p>
          {status?.trial_active && (
            <div className="pricing-trial-status" data-testid="pricing-trial-status">
              <Sparkles size={14} /> Prueba activa · quedan {status.days_left} día(s)
            </div>
          )}
          {!trialUsed && !status?.trial_active && (
            <button
              className="pricing-trial-btn"
              onClick={startTrial}
              disabled={busy === "trial"}
              data-testid="start-trial-button"
              style={{ display: "none" }}
            >
              <Zap size={16} /> {busy === "trial" ? "Activando…" : "Empezar prueba gratis de 7 días"}
            </button>
          )}
          {notice && <div className="pricing-notice success" data-testid="pricing-notice">{notice}</div>}
          {error && <div className="pricing-notice error" data-testid="pricing-error">{error}</div>}
        </section>

        <div className="pricing-interval-toggle" data-testid="pricing-interval-toggle" role="tablist">
          <button
            role="tab"
            aria-selected={interval === "monthly"}
            data-testid="pricing-interval-monthly"
            className={`pricing-interval-btn${interval === "monthly" ? " active" : ""}`}
            onClick={() => setInterval("monthly")}
          >
            Mensual
          </button>
          <button
            role="tab"
            aria-selected={interval === "yearly"}
            data-testid="pricing-interval-yearly"
            className={`pricing-interval-btn${interval === "yearly" ? " active" : ""}`}
            onClick={() => setInterval("yearly")}
          >
            Anual <span className="pricing-interval-savings">2 meses gratis</span>
          </button>
        </div>

        <section className="pricing-grid" data-testid="pricing-cards">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            const monthlyPrice = discount ? plan.price * (1 - discount / 100) : plan.price;
            const yearlyPrice = plan.price * 10; // 2 months free
            const yearlyDiscounted = discount ? yearlyPrice * (1 - discount / 100) : yearlyPrice;
            const showYearly = interval === "yearly";
            const displayPrice = showYearly ? yearlyDiscounted : monthlyPrice;
            const recurringType = showYearly ? "yearly_recurring" : "monthly_recurring";
            const manualType = showYearly ? "yearly_manual" : "monthly_manual";
            const manualHint = showYearly ? "Sin renovación automática · 12 meses de acceso" : "Sin renovación automática · 30 días de acceso";
            return (
              <article
                key={plan.id}
                className={`pricing-card${plan.highlight ? " highlight" : ""}${isCurrent ? " current" : ""}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.highlight && <span className="pricing-badge">Recomendado</span>}
                {isCurrent && <span className="pricing-badge current-badge">Tu plan actual</span>}
                <h2>{plan.name}</h2>
                <p className="pricing-tagline">{plan.tagline}</p>
                {plan.subLabel && (
                  <p className="pricing-tagline" style={{ marginTop: -8, fontStyle: "italic" }} data-testid={`plan-sublabel-${plan.id}`}>
                    {plan.subLabel}
                  </p>
                )}
                {plan.moralNote && (
                  <p className="pricing-tagline" style={{ marginTop: -6, fontStyle: "italic", color: "#64748b" }} data-testid={`plan-moralnote-${plan.id}`}>
                    {plan.moralNote}
                  </p>
                )}
                {plan.intro && (
                  <p className="pricing-tagline" style={{ marginTop: 6 }} data-testid={`plan-intro-${plan.id}`}>
                    {plan.intro}
                  </p>
                )}
                <div className="pricing-price">
                  {discount > 0 && plan.price > 0 && (
                    <s>${showYearly ? yearlyPrice : plan.price} MXN</s>
                  )}
                  <b>${Math.round(displayPrice).toLocaleString("es-MX")}</b>
                  <small>+ IVA · MXN/{showYearly ? "año" : "mes"}</small>
                  {showYearly && (
                    <div className="pricing-yearly-savings" data-testid={`plan-yearly-savings-${plan.id}`}>
                      Ahorras ${Math.round(plan.price * 2).toLocaleString("es-MX")} MXN · equivale a ${Math.round(displayPrice / 12).toLocaleString("es-MX")} MXN/mes + IVA
                    </div>
                  )}
                </div>
                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f}><Check size={14} color="#10b981" /> <span>{f}</span></li>
                  ))}
                  {(plan.excluded || []).map((f) => (
                    <li key={f} className="excluded"><X size={14} color="#94a3b8" /> <span>{f}</span></li>
                  ))}
                </ul>
                {plan.footerNote && (
                  <p style={{ font: '700 13px "Nunito", sans-serif', color: "#0f172a", marginTop: 10 }} data-testid={`plan-footernote-${plan.id}`}>
                    {plan.footerNote}
                  </p>
                )}
                <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                  <button
                    className={`pricing-cta${plan.highlight ? " primary" : ""}`}
                    onClick={() => selectPlan(plan.id, recurringType)}
                    disabled={isCurrent || busy === `${plan.id}:${recurringType}`}
                    data-testid={`select-plan-${plan.id}-recurring`}
                    title={showYearly ? "Se renueva automáticamente cada 12 meses. Cancela cuando quieras." : "Se renueva automáticamente cada mes. Cancela cuando quieras."}
                  >
                    {isCurrent ? "Plan actual" : (busy === `${plan.id}:${recurringType}` ? "…" : (showYearly ? "Renovación automática anual" : "Pago mensual automático"))}
                  </button>
                  <button
                    className="pricing-cta"
                    style={{ background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" }}
                    onClick={() => selectPlan(plan.id, manualType)}
                    disabled={busy === `${plan.id}:${manualType}`}
                    data-testid={`select-plan-${plan.id}-manual`}
                    title={showYearly ? "Pago único · 12 meses de acceso · sin renovación automática" : "Un solo pago · sin renovación automática · 30 días de acceso"}
                  >
                    {busy === `${plan.id}:${manualType}` ? "…" : (showYearly ? "Pagar solo este año" : "Pagar solo este mes")}
                  </button>
                  {plan.id === "privado" && (
                    <button
                      className="pricing-cta"
                      style={{ background: "#0f172a", color: "#fff" }}
                      onClick={() => setShowPrivadoModal(true)}
                      data-testid="privado-agenda-cita"
                    >
                      Agendar cita (Persona Moral)
                    </button>
                  )}
                  <small style={{ font: '500 11px "Nunito", sans-serif', color: "#94a3b8", textAlign: "center", marginTop: 2 }}>
                    {manualHint}
                  </small>
                </div>
              </article>
            );
          })}
        </section>

        <section className="pricing-promo" data-testid="pricing-promo">
          <h3>¿Tienes un código promocional?</h3>
          <div className="pricing-promo-form">
            <input
              type="text"
              placeholder="Escribe tu código"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              disabled={!!status?.promo_code}
              data-testid="promo-code-input"
            />
            <button
              onClick={redeem}
              disabled={!promoCode.trim() || promoBusy || !!status?.promo_code}
              data-testid="promo-redeem-button"
            >
              {promoBusy ? "Aplicando…" : "Aplicar"}
            </button>
          </div>
          {status?.promo_code && (
            <div className="pricing-notice success" data-testid="promo-active" style={{ marginTop: 14 }}>
              🎉 Código <b>{status.promo_code}</b> · {status.promo_discount}% de descuento · <b>De por vida</b>
            </div>
          )}
        </section>

        <LegalFooter variant="pricing" />
        {showPrivadoModal && <PrivadoModal onClose={() => setShowPrivadoModal(false)} />}
      </div>
    </AppShell>
  );
}

function PrivadoModal({ onClose }) {
  const [form, setForm] = useState({
    contact_type: "persona_moral",
    name: "", email: "", phone: "", company: "", rfc: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/agenda/privado", form);
      setOk(true);
    } catch (issue) {
      const detail = issue.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No pudimos enviar tu solicitud. Intenta de nuevo.");
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="privado-title"
      data-testid="privado-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 20, maxWidth: 520, width: "100%",
        padding: 28, boxShadow: "0 24px 80px rgba(15,23,42,0.35)", maxHeight: "90vh", overflowY: "auto" }}>
        <h2 id="privado-title" style={{ font: '900 22px "Nunito", sans-serif', color: "#0f172a", margin: "0 0 6px" }}>
          Agendar cita · Mi Contador Privado
        </h2>
        <p style={{ font: '500 13px "Nunito", sans-serif', color: "#64748b", margin: "0 0 18px" }}>
          Déjanos tus datos y un miembro del equipo te contactará para evaluar tu situación fiscal.
        </p>
        {ok ? (
          <div data-testid="privado-modal-success" style={{ background: "#dcfce7", color: "#065f46",
              padding: 16, borderRadius: 12, font: '700 14px "Nunito", sans-serif' }}>
            🎉 Recibimos tu solicitud. Te contactaremos muy pronto en <b>{form.email}</b>.
            <div style={{ marginTop: 12 }}>
              <button className="pricing-cta primary" onClick={onClose} data-testid="privado-modal-close-ok">
                Cerrar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
            <Field label="Tipo">
              <select value={form.contact_type} onChange={set("contact_type")} data-testid="privado-contact-type">
                <option value="persona_moral">Persona Moral</option>
                <option value="persona_fisica">Persona Física</option>
              </select>
            </Field>
            <Field label="Nombre completo *">
              <input required value={form.name} onChange={set("name")} data-testid="privado-name" />
            </Field>
            <Field label="Correo electrónico *">
              <input required type="email" value={form.email} onChange={set("email")} data-testid="privado-email" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Teléfono">
                <input value={form.phone} onChange={set("phone")} data-testid="privado-phone" />
              </Field>
              <Field label="RFC">
                <input value={form.rfc} onChange={set("rfc")} data-testid="privado-rfc"
                       style={{ textTransform: "uppercase" }} />
              </Field>
            </div>
            <Field label="Empresa / Razón Social">
              <input value={form.company} onChange={set("company")} data-testid="privado-company" />
            </Field>
            <Field label="Cuéntanos sobre tu situación (opcional)">
              <textarea rows={3} value={form.message} onChange={set("message")} data-testid="privado-message" />
            </Field>
            {error && <div style={{ color: "#dc2626", fontSize: 13 }} data-testid="privado-modal-error">{error}</div>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
              <button type="button" className="pricing-cta"
                      style={{ background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" }}
                      onClick={onClose} data-testid="privado-modal-cancel">
                Cancelar
              </button>
              <button type="submit" className="pricing-cta primary" disabled={busy} data-testid="privado-modal-submit">
                {busy ? "Enviando…" : "Enviar solicitud"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "grid", gap: 4, font: '700 12px "Nunito", sans-serif', color: "#334155" }}>
      {label}
      {children}
    </label>
  );
}
